import { useState, useEffect, useCallback } from "react";
import { LayoutDashboard, Users, UserCog, Disc3, PieChart, CreditCard, Music, DollarSign, LifeBuoy } from "lucide-react";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { api } from "@/lib/api";
import LabelOverview from "./LabelOverview";
import LabelRoster from "./LabelRoster";
import LabelTeam from "./LabelTeam";
import LabelCatalog from "./LabelCatalog";
import LabelSplits from "./LabelSplits";
import ArtistReleases from "@/components/artist/ArtistReleases";
import ArtistRoyalties from "@/components/artist/ArtistRoyalties";
import ArtistTickets from "@/components/support/ArtistTickets";
import BillingPanel from "@/components/billing/BillingPanel";

export default function LabelApp() {
  // Auto-open the correct tab when returning from Stripe OR when tier is pre-selected on landing.
  // - ?billing=return  → Billing (subscription roundtrip)
  // - ?tier=<...>      → Billing (landing-page pricing CTA)
  // - ?dolby_paid=<id> → My Releases (Stripe $40 add-on roundtrip)
  const initialTab = (() => {
    if (typeof window === "undefined") return "overview";
    const q = new URLSearchParams(window.location.search);
    if (q.get("billing") === "return") return "billing";
    if (q.get("tier")) return "billing";
    if (q.get("dolby_paid")) return "releases";
    return "overview";
  })();
  const [active, setActive] = useState(initialTab);
  const [ov, setOv] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/label/overview");
      setOv(data);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const nav = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "releases", label: "My Releases", icon: Music },
    { key: "roster", label: "Artists", icon: Users, badge: ov?.pending_invites },
    { key: "team", label: "Team", icon: UserCog },
    { key: "catalog", label: "Catalog", icon: Disc3, badge: ov?.catalog },
    { key: "splits", label: "Royalty Splits", icon: PieChart },
    { key: "royalties", label: "Royalties", icon: DollarSign },
    { key: "support", label: "Support", icon: LifeBuoy },
    { key: "billing", label: "Billing", icon: CreditCard },
  ];

  const titles = {
    overview: "Overview",
    releases: "My Releases",
    roster: "Artists",
    team: "Team",
    catalog: "Catalog",
    splits: "Royalty Splits",
    royalties: "Royalties",
    support: "Support",
    billing: "Billing",
  };

  return (
    <DashboardShell title={titles[active]} badge="Label" nav={nav} active={active} setActive={setActive}>
      {active === "overview" && <LabelOverview ov={ov} goto={setActive} />}
      {active === "releases" && <ArtistReleases />}
      {active === "roster" && <LabelRoster onChange={load} />}
      {active === "team" && <LabelTeam onChange={load} />}
      {active === "catalog" && <LabelCatalog />}
      {active === "splits" && <LabelSplits />}
      {active === "royalties" && <ArtistRoyalties />}
      {active === "support" && <ArtistTickets />}
      {active === "billing" && <BillingPanel tiers={["label"]} />}
    </DashboardShell>
  );
}
